import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CapturedPhoto } from '@core/models';

@Component({
  selector: 'app-photo-capture',
  imports: [CommonModule],
  templateUrl: './photo-capture.component.html',
  styleUrl: './photo-capture.component.scss',
})
export class PhotoCaptureComponent implements OnChanges, OnDestroy, OnInit {
  @Input() maxPhotos = 8;
  @Input() minPhotos = 1;
  @Input() title = 'Fotos';
  @Input() fileNamePrefix = 'reception';
  @Input() initialPhotos: CapturedPhoto[] = [];

  @Output() photosChange = new EventEmitter<CapturedPhoto[]>();

  @ViewChild('videoElement') private videoElement?: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') private canvasElement?: ElementRef<HTMLCanvasElement>;

  photos: CapturedPhoto[] = [];
  cameraError = '';
  startingCamera = false;
  cameraReady = false;
  cameraEnabled = false;
  flashEnabled = false;
  flashSupported = false;
  private cameraStream: MediaStream | null = null;
  private lastInitialPhotosRef: CapturedPhoto[] | null = null;

  async ngOnInit(): Promise<void> {
    await this.startCamera();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['initialPhotos']) {
      return;
    }

    const nextValue = this.initialPhotos ?? [];
    if (this.lastInitialPhotosRef === nextValue) {
      return;
    }

    this.photos = [...nextValue];
    this.lastInitialPhotosRef = nextValue;
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  async enableCamera(): Promise<void> {
    await this.startCamera();
  }

  disableCamera(): void {
    this.stopCamera();
    this.cameraEnabled = false;
  }

  async startCamera(): Promise<void> {
    this.cameraEnabled = true;
    this.cameraError = '';
    this.startingCamera = true;

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Este dispositivo no soporta captura por camara.');
      }

      this.stopCamera();
      this.cameraStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });

      const video = this.videoElement?.nativeElement;
      if (!video) {
        throw new Error('No fue posible iniciar la vista previa de la camara.');
      }

      video.srcObject = this.cameraStream;
      await video.play();
      this.cameraReady = true;
      this.checkFlashSupport();
    } catch (error) {
      this.cameraReady = false;
      this.cameraError =
        error instanceof Error ? error.message : 'No fue posible acceder a la camara.';
    } finally {
      this.startingCamera = false;
    }
  }

  async toggleFlash(): Promise<void> {
    const track = this.cameraStream?.getVideoTracks()[0];
    if (!track || !this.flashSupported) {
      return;
    }

    const nextValue = !this.flashEnabled;

    try {
      await track.applyConstraints({
        advanced: [{ torch: nextValue } as MediaTrackConstraintSet],
      });
      this.flashEnabled = nextValue;
    } catch {
      this.flashEnabled = false;
    }
  }

  private checkFlashSupport(): void {
    this.flashEnabled = false;
    this.flashSupported = false;

    const track = this.cameraStream?.getVideoTracks()[0];
    if (!track?.getCapabilities) {
      return;
    }

    const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
    this.flashSupported = !!capabilities.torch;
  }

  async capturePhoto(): Promise<void> {
    if (!this.cameraReady || this.photos.length >= this.maxPhotos) {
      return;
    }

    const video = this.videoElement?.nativeElement;
    const canvas = this.canvasElement?.nativeElement;
    if (!video || !canvas) {
      return;
    }

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    context.drawImage(video, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.9);
    });

    if (!blob) {
      return;
    }

    const fileName = `${this.fileNamePrefix}-${Date.now()}-${this.photos.length + 1}.jpg`;
    const file = new File([blob], fileName, { type: 'image/jpeg' });
    const previewUrl = URL.createObjectURL(file);

    this.photos.push({ fileName, file, previewUrl });
    this.photosChange.emit([...this.photos]);
  }

  removePhoto(index: number): void {
    const removed = this.photos.splice(index, 1);
    if (removed[0]?.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(removed[0].previewUrl);
    }
    this.photosChange.emit([...this.photos]);
  }

  private stopCamera(): void {
    if (!this.cameraStream) {
      return;
    }

    for (const track of this.cameraStream.getTracks()) {
      track.stop();
    }
    this.cameraStream = null;
    this.cameraReady = false;
    this.flashEnabled = false;
    this.flashSupported = false;
  }
}
